/**
 * WHAT: User management interface for DEV_ADMINs to manage application users, roles,
 *       and Azure AD group memberships with push-to-Azure capability.
 * WHY:  DAMA's RBAC requires users to be assigned roles and placed in Azure AD security
 *       groups. This component provides a single screen to create users, change roles,
 *       manage group memberships, and push changes back to Azure AD so the groups stay
 *       in sync with DAMA-side assignments.
 */

import React, { useState, useEffect, useCallback } from 'react';
import API_CONFIG from '../config/api.config';
import { useSafeTimeout } from '../hooks';
import './UserManagement.css';

function UserManagement() {
  // Core data state
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Filter state
  const [filterRole, setFilterRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);

  // Selected user state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);

  // Create form state
  const [enterpriseSearch, setEnterpriseSearch] = useState('');
  const [enterpriseResults, setEnterpriseResults] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    full_name: '',
    role_id: '',
    enterprise_user_id: ''
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    role_id: '',
    email: '',
    full_name: '',
    is_active: true
  });

  // Push-to-Azure state
  const [pushing, setPushing] = useState({});
  const [pushResults, setPushResults] = useState(null);

  const { setSafeTimeout } = useSafeTimeout();

  // ---- Data fetching ----

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('active_only', !showInactive);
      if (filterRole) params.append('role_id', filterRole);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`users/?${params.toString()}`)
      );

      if (!response.ok) throw new Error(`Failed to fetch users: ${response.statusText}`);

      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      } else {
        throw new Error(data.error || 'Failed to fetch users');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [showInactive, filterRole, searchTerm]);

  const fetchRoles = async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('users/roles/'));
      if (!response.ok) throw new Error('Failed to fetch roles');
      const data = await response.json();
      if (data.success) {
        setRoles(data.roles);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers]);

  // ---- Enterprise user search (for create) ----

  useEffect(() => {
    if (enterpriseSearch.length < 2) {
      setEnterpriseResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          API_CONFIG.getDatabasesEndpoint(`users/enterprise-search/?q=${encodeURIComponent(enterpriseSearch)}`)
        );
        const data = await response.json();
        if (data.success) {
          setEnterpriseResults(data.users);
        }
      } catch (err) {
        console.error('Enterprise search error:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [enterpriseSearch]);

  // ---- Success/error helpers ----

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setSafeTimeout(() => setSuccessMessage(null), 3000);
  };

  // ---- Create user ----

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('users/create/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm)
      });

      const data = await response.json();
      if (data.success) {
        showSuccess(`User created: ${createForm.username}`);
        setShowCreateModal(false);
        resetCreateForm();
        fetchUsers();
      } else {
        throw new Error(data.error || 'Failed to create user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({ username: '', email: '', full_name: '', role_id: '', enterprise_user_id: '' });
    setSelectedEnterprise(null);
    setEnterpriseSearch('');
    setEnterpriseResults([]);
  };

  const selectEnterpriseUser = (eu) => {
    setSelectedEnterprise(eu);
    setCreateForm({
      ...createForm,
      username: eu.email ? eu.email.split('@')[0] : eu.display_name,
      email: eu.email || '',
      full_name: eu.display_name || '',
      enterprise_user_id: eu.user_id
    });
    setEnterpriseResults([]);
    setEnterpriseSearch('');
  };

  // ---- Edit user ----

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      role_id: user.role_id || '',
      email: user.email || '',
      full_name: user.full_name || '',
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`users/${selectedUser.app_user_id}/update/`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editForm)
        }
      );

      const data = await response.json();
      if (data.success) {
        showSuccess(`User updated: ${selectedUser.username}`);
        setShowEditModal(false);
        fetchUsers();
      } else {
        throw new Error(data.error || 'Failed to update user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- Delete user ----

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    try {
      setError(null);
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`users/${selectedUser.app_user_id}/delete/`),
        { method: 'DELETE' }
      );

      const data = await response.json();
      if (data.success) {
        showSuccess(data.message);
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        throw new Error(data.error || 'Failed to delete user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- Group management ----

  const openGroupsModal = async (user) => {
    setSelectedUser(user);
    setShowGroupsModal(true);
    setPushResults(null);

    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`users/${user.app_user_id}/groups/`)
      );
      const data = await response.json();
      if (data.success) {
        setUserGroups(data.groups);
        setAvailableGroups(data.available_groups);
      } else {
        setError(data.error || 'Failed to load groups');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddToGroup = async (groupId) => {
    try {
      setError(null);
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`users/${selectedUser.app_user_id}/groups/add/`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ group_id: groupId })
        }
      );

      const data = await response.json();
      if (data.success) {
        showSuccess(data.message);
        // Refresh group lists
        openGroupsModal(selectedUser);
        fetchUsers();
      } else {
        throw new Error(data.error || 'Failed to add to group');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveFromGroup = async (groupId) => {
    try {
      setError(null);
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`users/${selectedUser.app_user_id}/groups/remove/`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ group_id: groupId })
        }
      );

      const data = await response.json();
      if (data.success) {
        showSuccess(data.message);
        openGroupsModal(selectedUser);
        fetchUsers();
      } else {
        throw new Error(data.error || 'Failed to remove from group');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- Push to Azure AD ----

  const handlePushToAzure = async (groupId, groupName) => {
    try {
      setPushing(prev => ({ ...prev, [groupId]: true }));
      setError(null);
      setPushResults(null);

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`azure-ad/groups/${groupId}/push/`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = await response.json();
      if (data.success) {
        setPushResults({ groupName, ...data.push_results });
        showSuccess(`Pushed ${groupName} to Azure AD`);
      } else {
        throw new Error(data.error || 'Failed to push to Azure AD');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPushing(prev => ({ ...prev, [groupId]: false }));
    }
  };

  // ---- Helpers ----

  const getRoleBadgeClass = (roleId) => {
    if (!roleId) return 'um-role-badge';
    if (roleId === 'DEV_ADMIN') return 'um-role-badge um-dev-admin';
    if (roleId === 'DAMA_ADMIN') return 'um-role-badge um-uar-admin';
    if (roleId === 'ATTESTER') return 'um-role-badge um-attester';
    if (roleId.endsWith('_DB_ADMIN')) return 'um-role-badge um-dba';
    return 'um-role-badge';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // ---- Loading state ----

  if (loading && users.length === 0) {
    return (
      <div className="um-container">
        <div className="um-loading">
          <div className="um-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="um-container">
      {/* Header */}
      <div className="um-page-header">
        <div className="um-header-left">
          <h1><i className="fas fa-users-cog"></i> User Management</h1>
          <p className="um-header-subtitle">
            Manage application users, role assignments, and Azure AD group memberships
          </p>
        </div>
        <div className="um-header-right">
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <i className="fas fa-user-plus"></i> Add User
          </button>
        </div>
      </div>

      {/* Alerts */}
      {successMessage && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i> {successMessage}
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i> {error}
          <button className="um-alert-dismiss" onClick={() => setError(null)}>x</button>
        </div>
      )}

      {/* Filters */}
      <div className="um-filters">
        <div className="um-filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by name, email, username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="um-filter-group">
          <label>Role:</label>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            {roles.map(r => (
              <option key={r.role_id} value={r.role_id}>{r.role_name}</option>
            ))}
          </select>
        </div>
        <div className="um-filter-group um-checkbox-filter">
          <label>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show Inactive
          </label>
        </div>
        <div className="um-filter-group">
          <span className="um-user-count">{users.length} user{users.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Users Table */}
      <div className="um-table-container">
        {users.length === 0 ? (
          <div className="um-empty-state">
            <i className="fas fa-users"></i>
            <p>No users found. Add a user to get started.</p>
          </div>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Groups</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.app_user_id} className={!user.is_active ? 'um-inactive-row' : ''}>
                  <td><strong>{user.username}</strong></td>
                  <td>{user.full_name || '-'}</td>
                  <td className="um-email-cell">{user.email || '-'}</td>
                  <td>
                    <span className={getRoleBadgeClass(user.role_id)}>
                      {user.role_name || user.role_id || 'None'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="um-btn-link"
                      onClick={() => openGroupsModal(user)}
                      title={user.group_names || 'No groups'}
                    >
                      {user.group_count || 0} group{user.group_count !== 1 ? 's' : ''}
                    </button>
                  </td>
                  <td>
                    <span className={user.is_active ? 'text-success' : 'text-danger'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="um-date-cell">{formatDate(user.last_login_date)}</td>
                  <td>
                    <div className="um-action-buttons">
                      <button className="um-btn-icon" onClick={() => openEditModal(user)} title="Edit User">
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="um-btn-icon" onClick={() => openGroupsModal(user)} title="Manage Groups">
                        <i className="fas fa-layer-group"></i>
                      </button>
                      <button className="um-btn-icon um-btn-icon-danger" onClick={() => openDeleteModal(user)} title="Deactivate User">
                        <i className="fas fa-user-slash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ==================== CREATE USER MODAL ==================== */}
      {showCreateModal && (
        <div className="um-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="um-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><i className="fas fa-user-plus"></i> Add New User</h2>
              <button className="um-modal-close" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>x</button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="um-modal-body">
                {/* Enterprise user search */}
                <div className="um-form-group">
                  <label>Link to Enterprise User (optional)</label>
                  <input
                    type="text"
                    placeholder="Search by name, email, or employee ID..."
                    value={enterpriseSearch}
                    onChange={(e) => setEnterpriseSearch(e.target.value)}
                  />
                  {selectedEnterprise && (
                    <div className="um-selected-enterprise">
                      <i className="fas fa-user-check"></i>
                      <span>{selectedEnterprise.display_name} ({selectedEnterprise.email})</span>
                      <button type="button" onClick={() => { setSelectedEnterprise(null); setCreateForm({...createForm, enterprise_user_id: ''}); }}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                  {enterpriseResults.length > 0 && !selectedEnterprise && (
                    <div className="um-search-results">
                      {enterpriseResults.map(eu => (
                        <div
                          key={eu.user_id}
                          className={`um-search-result-item ${eu.already_provisioned ? 'um-already-provisioned' : ''}`}
                          onClick={() => !eu.already_provisioned && selectEnterpriseUser(eu)}
                        >
                          <div className="um-search-result-main">
                            <strong>{eu.display_name}</strong>
                            <span className="um-search-result-email">{eu.email}</span>
                          </div>
                          <div className="um-search-result-meta">
                            <span>{eu.department || ''}</span>
                            {eu.already_provisioned && (
                              <span className="um-already-badge">Already exists ({eu.current_role})</span>
                            )}
                            {!eu.account_enabled && (
                              <span className="um-disabled-badge">Disabled</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="um-form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={createForm.username}
                    onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                    required
                    placeholder="e.g., john.smith"
                  />
                </div>

                <div className="um-form-row">
                  <div className="um-form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={createForm.full_name}
                      onChange={(e) => setCreateForm({...createForm, full_name: e.target.value})}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                  <div className="um-form-group">
                    <label>Email</label>
                    <input
                      type="text"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                      placeholder="e.g., john.smith@company.com"
                    />
                  </div>
                </div>

                <div className="um-form-group">
                  <label>Role *</label>
                  <select
                    value={createForm.role_id}
                    onChange={(e) => setCreateForm({...createForm, role_id: e.target.value})}
                    required
                  >
                    <option value="">Select a role...</option>
                    {roles.map(r => (
                      <option key={r.role_id} value={r.role_id}>
                        {r.role_name} {r.role_description ? `- ${r.role_description}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="um-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-user-plus"></i> Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== EDIT USER MODAL ==================== */}
      {showEditModal && selectedUser && (
        <div className="um-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="um-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><i className="fas fa-user-edit"></i> Edit User: {selectedUser.username}</h2>
              <button className="um-modal-close" onClick={() => setShowEditModal(false)}>x</button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="um-modal-body">
                <div className="um-form-group">
                  <label>Username (Read-Only)</label>
                  <input type="text" value={selectedUser.username} disabled className="um-readonly" />
                </div>

                <div className="um-form-row">
                  <div className="um-form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    />
                  </div>
                  <div className="um-form-group">
                    <label>Email</label>
                    <input
                      type="text"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="um-form-group">
                  <label>Role</label>
                  <select
                    value={editForm.role_id}
                    onChange={(e) => setEditForm({...editForm, role_id: e.target.value})}
                  >
                    <option value="">Select a role...</option>
                    {roles.map(r => (
                      <option key={r.role_id} value={r.role_id}>
                        {r.role_name} {r.role_description ? `- ${r.role_description}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="um-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                    />
                    {' '}Active
                  </label>
                </div>
              </div>

              <div className="um-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save"></i> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== DELETE CONFIRMATION MODAL ==================== */}
      {showDeleteModal && selectedUser && (
        <div className="um-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="um-modal-content um-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="um-modal-header um-modal-header-danger">
              <h2><i className="fas fa-user-slash"></i> Deactivate User</h2>
              <button className="um-modal-close" onClick={() => setShowDeleteModal(false)}>x</button>
            </div>
            <div className="um-modal-body">
              <p>Are you sure you want to deactivate:</p>
              <p className="um-delete-highlight"><strong>{selectedUser.username}</strong> ({selectedUser.full_name})</p>
              <p className="um-warning-text">
                <i className="fas fa-exclamation-triangle"></i>{' '}
                The user will no longer be able to log in or access any features.
              </p>
            </div>
            <div className="um-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteUser}>
                <i className="fas fa-user-slash"></i> Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== GROUPS MANAGEMENT MODAL ==================== */}
      {showGroupsModal && selectedUser && (
        <div className="um-modal-overlay" onClick={() => setShowGroupsModal(false)}>
          <div className="um-modal-content um-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><i className="fas fa-layer-group"></i> Groups: {selectedUser.username}</h2>
              <button className="um-modal-close" onClick={() => setShowGroupsModal(false)}>x</button>
            </div>
            <div className="um-modal-body">
              {!selectedUser.enterprise_user_id ? (
                <div className="um-warning-box">
                  <i className="fas fa-exclamation-triangle"></i>{' '}
                  This user has no linked enterprise identity. Link them to an enterprise user first to manage group memberships.
                </div>
              ) : (
                <>
                  {/* Push results */}
                  {pushResults && (
                    <div className="um-push-results">
                      <h4><i className="fas fa-cloud-upload-alt"></i> Push Results: {pushResults.groupName}</h4>
                      <div className="um-push-stats">
                        <span className="um-push-stat um-stat-added">+{pushResults.members_added} added</span>
                        <span className="um-push-stat um-stat-removed">-{pushResults.members_removed} removed</span>
                        <span className="um-push-stat um-stat-unchanged">={pushResults.members_unchanged} unchanged</span>
                      </div>
                      {pushResults.errors && pushResults.errors.length > 0 && (
                        <div className="um-push-errors">
                          {pushResults.errors.map((err, i) => (
                            <div key={i} className="um-push-error"><i className="fas fa-exclamation-circle"></i> {err}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Current groups */}
                  <div className="um-groups-section">
                    <h3>Current Group Memberships ({userGroups.length})</h3>
                    {userGroups.length === 0 ? (
                      <p className="um-muted">No group memberships.</p>
                    ) : (
                      <table className="um-groups-table">
                        <thead>
                          <tr>
                            <th>Group Name</th>
                            <th>Mapped Role</th>
                            <th>Assigned</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userGroups.map(g => (
                            <tr key={g.group_id}>
                              <td>
                                <strong>{g.group_name}</strong>
                                {g.description && <div className="um-group-desc">{g.description}</div>}
                              </td>
                              <td>
                                <span className={getRoleBadgeClass(g.mapped_role_id)}>
                                  {g.role_name || g.mapped_role_id}
                                </span>
                              </td>
                              <td className="um-date-cell">{formatDate(g.assigned_date)}</td>
                              <td>
                                <div className="um-action-buttons">
                                  <button
                                    className="um-btn-icon um-btn-icon-danger"
                                    onClick={() => handleRemoveFromGroup(g.group_id)}
                                    title="Remove from group"
                                  >
                                    <i className="fas fa-user-minus"></i>
                                  </button>
                                  <button
                                    className="um-btn-icon um-btn-icon-azure"
                                    onClick={() => handlePushToAzure(g.group_id, g.group_name)}
                                    disabled={pushing[g.group_id]}
                                    title="Push this group to Azure AD"
                                  >
                                    {pushing[g.group_id]
                                      ? <i className="fas fa-spinner fa-spin"></i>
                                      : <i className="fas fa-cloud-upload-alt"></i>
                                    }
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Available groups to add */}
                  <div className="um-groups-section">
                    <h3>Available Groups ({availableGroups.length})</h3>
                    {availableGroups.length === 0 ? (
                      <p className="um-muted">User is a member of all active groups.</p>
                    ) : (
                      <table className="um-groups-table">
                        <thead>
                          <tr>
                            <th>Group Name</th>
                            <th>Mapped Role</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableGroups.map(g => (
                            <tr key={g.group_id}>
                              <td>
                                <strong>{g.group_name}</strong>
                                {g.description && <div className="um-group-desc">{g.description}</div>}
                              </td>
                              <td>
                                <span className={getRoleBadgeClass(g.mapped_role_id)}>
                                  {g.role_name || g.mapped_role_id}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="um-btn-icon um-btn-icon-success"
                                  onClick={() => handleAddToGroup(g.group_id)}
                                  title="Add to group"
                                >
                                  <i className="fas fa-user-plus"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="um-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowGroupsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
